import type { Client } from "@sentry/core";
import * as Sentry from "@sentry/react";
import axios from "axios";

import { useUserSettings } from "@/hooks/useUserSettings";
import { ReactNode, createContext, useEffect, useState } from "react";

export const SentryContext = createContext<Client | undefined>(undefined);

let sentryClient: Client | undefined = undefined;
let isInitialized = false;

const sentryInit = () => {
  if (process.env.REACT_APP_SENTRY_DSN && !isInitialized) {
    sentryClient = Sentry.init({
      dsn: process.env.REACT_APP_SENTRY_DSN,
      sendDefaultPii: true,
      integrations: [
        Sentry.browserTracingIntegration(),
        Sentry.replayIntegration(),
        Sentry.reportingObserverIntegration(),
        Sentry.httpClientIntegration(),
        Sentry.captureConsoleIntegration({
          levels: ["error", "warn", "info"],
        }),
        Sentry.extraErrorDataIntegration({
          depth: 10,
        }),
        Sentry.contextLinesIntegration(),
        Sentry.browserProfilingIntegration(),
      ],
      enableLogs: true,
      tracesSampleRate: 1.0,
      replaysSessionSampleRate: 0.1,
      replaysOnErrorSampleRate: 1.0,
      attachStacktrace: true,
      tracePropagationTargets: [
        /^.*\.root-sustainability\.com\/.*$/,
        /^.*\.amazonaws\.com\/.*$/,
        /^localhost:8000\/.*$/,
      ],
      environment: process.env.REACT_APP_SENTRY_ENVIRONMENT || "local",
    });

    // Set up axios interceptor only once
    axios.interceptors.request.use(async (config) => {
      const traceData = Sentry.getTraceData();
      config.headers["sentry-trace"] = traceData["sentry-trace"];
      config.headers["baggage"] = traceData.baggage;

      return config;
    });

    isInitialized = true;
  }
  return sentryClient;
};

export default function SentryProvider({ children }: { children: ReactNode }) {
  const [client, setClient] = useState<Client | undefined>(undefined);
  const { data } = useUserSettings();

  const userId = data?.user_id;
  const companyId = data?.company_id;

  useEffect(() => {
    const initializedClient = sentryInit();
    setClient(initializedClient);
  }, []);

  useEffect(() => {
    if (client && userId && companyId) {
      Sentry.setContext("user", {
        user_id: String(userId),
        company_id: String(companyId),
      });
    }
  }, [client, userId, companyId]);

  return <SentryContext.Provider value={client}>{children}</SentryContext.Provider>;
}
